import { useMemo } from 'react';
import lightenDarkenColor from '../utils/lightenDarkenColor';
import getColorBasedOnBgColor from '../utils/getColorBasedOnBgColor';
import { COLORS } from '../styles/variables';
import type { VehicleInfo } from '../types';
import {
  SYS_COLOR_SURFACE_NEUTRAL_800_LIGHT,
  SEM_COLOR_FEEDBACK_SURFACE_DEFAULT_DARK,
  SYS_COLOR_SURFACE_NEUTRAL_400_LIGHT,
  SYS_COLOR_SURFACE_NEUTRAL_300_LIGHT,
  SYS_COLOR_SURFACE_NEUTRAL_500_LIGHT,
  SYS_COLOR_SURFACE_NEUTRAL_200_LIGHT,
} from '@oneaudi/unified-web-common';

const usePalette = (
  vehicleData: VehicleInfo | undefined,
): {
  backgroundColor: string;
  darkenedBackgroundColor: string;
  textColor: string;
  statusTextColor: string;
  statusBgColor: string;
  popUpBgColor: string;
} => {
  const backgroundColor = vehicleData?.dominantExteriorColor || '#999999';

  const darkenedBackgroundColor = useMemo(
    () => `#${lightenDarkenColor(backgroundColor, -7)}`,
    [backgroundColor],
  );

  const textColor = useMemo(
    () =>
      getColorBasedOnBgColor(
        backgroundColor,
        SYS_COLOR_SURFACE_NEUTRAL_800_LIGHT,
        SEM_COLOR_FEEDBACK_SURFACE_DEFAULT_DARK,
      ),
    [backgroundColor],
  );

  const statusTextColor = useMemo(
    () =>
      getColorBasedOnBgColor(
        backgroundColor,
        SYS_COLOR_SURFACE_NEUTRAL_400_LIGHT,
        SYS_COLOR_SURFACE_NEUTRAL_300_LIGHT,
      ),
    [backgroundColor],
  );

  const statusBgColor = useMemo(
    () =>
      getColorBasedOnBgColor(
        backgroundColor,
        COLORS.whiteOpacity,
        SYS_COLOR_SURFACE_NEUTRAL_500_LIGHT,
      ),
    [backgroundColor],
  );

  const popUpBgColor = useMemo(
    () =>
      getColorBasedOnBgColor(
        backgroundColor,
        SEM_COLOR_FEEDBACK_SURFACE_DEFAULT_DARK,
        SYS_COLOR_SURFACE_NEUTRAL_200_LIGHT,
      ),
    [backgroundColor],
  );

  const paletteBag = useMemo(
    () => ({
      backgroundColor,
      darkenedBackgroundColor,
      textColor,
      statusTextColor,
      statusBgColor,
      popUpBgColor,
    }),
    [
      backgroundColor,
      darkenedBackgroundColor,
      popUpBgColor,
      statusBgColor,
      statusTextColor,
      textColor,
    ],
  );

  return paletteBag;
};

export default usePalette;
